import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { PlayArrow as PlayIcon, SmartToy as AgentIcon } from '@mui/icons-material';
import { agentService } from '../services/agentService';
import type { AgentDefinitionDto } from '../types/agent';

export const OrchestratorPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AgentDefinitionDto | null>(null);

  useEffect(() => {
    agentService
      .getAgents({ pageSize: 200, isActive: true })
      .then((res) => setAgents(res.items))
      .finally(() => setLoading(false));
  }, []);

  const handleLaunch = () => {
    if (selected) {
      navigate(`/orchestrator/${selected.id}`);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Multi-Agent Orchestrator
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Pick an orchestrator agent and attach sub-agents. The orchestrator will delegate tasks to
        sub-agents via tool calls, with live progress in a Cursor-style timeline.
      </Typography>

      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Select Orchestrator Agent
        </Typography>
        <Autocomplete
          options={agents}
          getOptionLabel={(o) => `${o.name} (${o.slug})`}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          loading={loading}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <AgentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">{option.slug}</Typography>
              </Box>
              {option.category && (
                <Chip label={option.category} size="small" variant="outlined" sx={{ ml: 'auto' }} />
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agent"
              placeholder="Search agents..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: 3 }}
        />
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayIcon />}
          onClick={handleLaunch}
          disabled={!selected}
          fullWidth
        >
          Launch Orchestrator
        </Button>
      </Card>
    </Box>
  );
};
